'use client';

import * as React from 'react';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useTaxonomy, useTaxonomyCategory } from '@/hooks/useTaxonomy';
import type { TaxonomyCache, TaxonomyCategory, TaxonomyItem } from '@/lib/adapters';
import { getTaxonomyItemsForCategory } from '@/lib/utils/taxonomy-cascade';
import { cn } from '@/lib/utils';

const OTHER_VALUE = '__other__';
const MOBILE_QUERY = '(max-width: 639px)';
const LARGE_RESULT_LIMIT = 50;

type ItemRecord = Record<string, unknown>;

export interface TaxonomyComboboxProps {
  /**
   * Searchable single-select for high-cardinality taxonomy fields.
   * Prefer native `TaxonomySelect` below roughly 15 options; mobile native
   * selection is faster for short lists. Use this combobox for ranks,
   * certifications, skills, shore positions, vessel types, and similar 90+
   * option fields where search materially improves completion.
   */
  category: TaxonomyCategory;
  value: string | null;
  onChange: (value: string | null) => void;
  valueField?: 'code' | 'id';
  parentField?: string;
  parentValue?: string | number | null;
  allowEmptyParent?: boolean;
  groupByField?: string;
  groupByLabelMap?: Record<string, string>;
  filter?: (item: TaxonomyItem) => boolean;
  filterFn?: (item: TaxonomyItem) => boolean;
  helperText?: string;
  allowOther?: boolean;
  otherOptionLabel?: string;
  otherOptionValue?: string;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  otherPlaceholder?: string;
  label?: string;
  placeholder?: string;
  parentPlaceholder?: string;
  emptyPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  wrapperClassName?: string;
  comboboxClassName?: string;
  labelClassName?: string;
  helperTextClassName?: string;
}

interface RankedItem {
  item: TaxonomyItem;
  rank: number;
  matchedAlias?: string;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

function optionValue(item: TaxonomyItem, valueField: 'code' | 'id') {
  return valueField === 'id' ? String(item.id) : item.code;
}

function getItemField(item: TaxonomyItem, field: string) {
  return (item as unknown as ItemRecord)[field];
}

function hasParentValue(parentValue: string | number | null | undefined) {
  return parentValue !== undefined && parentValue !== null && parentValue !== '';
}

function getHierarchyCategory(parentField: string | undefined): TaxonomyCategory | null {
  if (parentField === 'vesselCategory') return 'vesselCategories';
  if (parentField === 'shoreCategory') return 'shoreCategories';
  return null;
}

function filterBySearch(items: TaxonomyItem[], search: string): RankedItem[] {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) return items.map((item) => ({ item, rank: 0 }));

  return items
    .reduce<RankedItem[]>((matches, item) => {
      const label = String(item.label ?? '').toLocaleLowerCase();
      const code = String(item.code ?? '').toLocaleLowerCase();
      const aliases = Array.isArray(item.aliases) ? item.aliases : [];
      const matchedAlias = aliases.find((alias) => alias.toLocaleLowerCase().includes(needle));
      const labelStarts = label.startsWith(needle);
      const codeStarts = code.startsWith(needle);
      const aliasStarts = matchedAlias?.toLocaleLowerCase().startsWith(needle) ?? false;
      const labelIncludes = label.includes(needle);
      const codeIncludes = code.includes(needle);

      if (!labelIncludes && !codeIncludes && !matchedAlias) return matches;

      matches.push({
        item,
        rank: labelStarts || codeStarts || aliasStarts ? 0 : 1,
        matchedAlias,
      });
      return matches;
    }, [])
    .sort((a, b) => a.rank - b.rank || a.item.label.localeCompare(b.item.label))
}

function uniqueItems(items: TaxonomyItem[]) {
  const seen = new Map<number, TaxonomyItem>();
  for (const item of items) {
    if (!seen.has(item.id)) seen.set(item.id, item);
  }
  return Array.from(seen.values());
}

function groupItems(
  items: RankedItem[],
  groupByField: string | undefined,
  groupByLabelMap: Record<string, string> | undefined
) {
  if (!groupByField) return null;

  const groups = new Map<string, RankedItem[]>();
  for (const rankedItem of items) {
    const item = rankedItem.item;
    const rawValue = String(getItemField(item, groupByField) ?? 'Other');
    if (!groups.has(rawValue)) groups.set(rawValue, []);
    groups.get(rawValue)?.push(rankedItem);
  }

  return Array.from(groups.entries()).sort(([a], [b]) => {
    const labelA = groupByLabelMap?.[a] ?? a;
    const labelB = groupByLabelMap?.[b] ?? b;
    return labelA.localeCompare(labelB);
  });
}

export default function TaxonomyCombobox({
  category,
  value,
  onChange,
  valueField = 'code',
  parentField,
  parentValue,
  allowEmptyParent = false,
  groupByField,
  groupByLabelMap,
  filter,
  filterFn,
  helperText,
  allowOther = false,
  otherOptionLabel = 'Other (not listed)',
  otherOptionValue = OTHER_VALUE,
  otherValue = '',
  onOtherChange,
  otherPlaceholder = 'Please specify...',
  label,
  placeholder = 'Search and select...',
  parentPlaceholder = 'Select parent first',
  emptyPlaceholder = 'No options available',
  disabled = false,
  required = false,
  wrapperClassName = '',
  comboboxClassName = '',
  labelClassName = '',
  helperTextClassName = '',
}: TaxonomyComboboxProps) {
  const { items, isLoading } = useTaxonomyCategory(category);
  const { data: taxonomy } = useTaxonomy();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [previousValue, setPreviousValue] = React.useState<string | null>(value);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const helperId = React.useId();
  const listId = React.useId();
  const statusId = React.useId();
  const hierarchyCategory = getHierarchyCategory(parentField);
  const hasParent = parentField !== undefined;
  const isParentSelected = hasParentValue(parentValue);
  const isCascadeDisabled = hasParent && !isParentSelected && !allowEmptyParent;

  const parentOptions = React.useMemo(() => {
    if (!taxonomy || !hierarchyCategory) return [];
    return (taxonomy[hierarchyCategory as keyof TaxonomyCache] ?? []) as TaxonomyItem[];
  }, [taxonomy, hierarchyCategory]);

  const filteredItems = React.useMemo(() => {
    let result = items;

    if (parentField !== undefined) {
      if (!isParentSelected) {
        result = allowEmptyParent ? items : [];
      } else if (hierarchyCategory && parentOptions.length > 0) {
        result = getTaxonomyItemsForCategory(
          Number(parentValue),
          parentOptions,
          items,
          parentField as keyof TaxonomyItem
        );
      } else {
        result = items.filter((item) => Number(getItemField(item, parentField)) === Number(parentValue));
      }
    }

    const predicate = filterFn ?? filter;
    if (predicate) result = result.filter(predicate);
    return uniqueItems(result);
  }, [allowEmptyParent, filter, filterFn, hierarchyCategory, isParentSelected, items, parentField, parentOptions, parentValue]);

  const selectedItem = React.useMemo(
    () => filteredItems.find((item) => optionValue(item, valueField) === String(value)) ?? null,
    [filteredItems, value, valueField]
  );
  const isOtherActive = allowOther && String(value) === String(otherOptionValue);
  const searchedItems = React.useMemo(() => filterBySearch(filteredItems, search), [filteredItems, search]);
  const visibleItems = search.trim() || searchedItems.length <= LARGE_RESULT_LIMIT
    ? searchedItems
    : searchedItems.slice(0, LARGE_RESULT_LIMIT);
  const groups = React.useMemo(
    () => groupItems(visibleItems, groupByField, groupByLabelMap),
    [groupByField, groupByLabelMap, visibleItems]
  );
  const activeDescendant = visibleItems[0] ? `${listId}-${visibleItems[0].item.id}` : undefined;
  const cascadeLabel = React.useMemo(() => {
    if (!hasParent || !isParentSelected || !parentOptions.length) return null;
    const parent = parentOptions.find((item) => String(item.id) === String(parentValue) || item.code === String(parentValue));
    if (!parent) return null;
    return `${filteredItems.length} ${categoryLabel(category)} in ${parent.label}`;
  }, [category, filteredItems.length, hasParent, isParentSelected, parentOptions, parentValue]);

  const isDisabled = disabled || isLoading || isCascadeDisabled;
  const displayValue = isOtherActive ? otherOptionLabel : selectedItem?.label;
  const activePlaceholder = isCascadeDisabled
    ? parentPlaceholder
    : hasParent && filteredItems.length === 0
      ? emptyPlaceholder
      : placeholder;
  const describedBy = [helperText ? helperId : null, cascadeLabel ? statusId : null].filter(Boolean).join(' ') || undefined;

  const closeAndRestore = React.useCallback(() => {
    onChange(previousValue);
    setOpen(false);
    setSearch('');
  }, [onChange, previousValue]);

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    if (isDisabled) return;
    if (nextOpen) {
      setPreviousValue(value);
      setSearch('');
    }
    setOpen(nextOpen);
  }, [isDisabled, value]);

  const selectValue = React.useCallback((nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      handleOpenChange(true);
    }
  };

  const list = (
    <TaxonomyComboboxList
      activeDescendant={activeDescendant}
      allowOther={allowOther}
      groupByLabelMap={groupByLabelMap}
      groups={groups}
      isOtherActive={isOtherActive}
      listId={listId}
      onEscape={closeAndRestore}
      onSelect={selectValue}
      otherOptionLabel={otherOptionLabel}
      otherOptionValue={otherOptionValue}
      search={search}
      setOpen={setOpen}
      setSearch={setSearch}
      value={value}
      valueField={valueField}
      visibleItems={visibleItems}
      totalItems={searchedItems.length}
    />
  );

  return (
    <div className={wrapperClassName}>
      {label && (
        <label className={cn('mb-1.5 block text-sm font-medium text-foreground', labelClassName)}>
          {label}
          {required && <span className="ml-1 text-neon-magenta">*</span>}
        </label>
      )}

      {isLoading ? (
        <div className="h-10 w-full animate-pulse rounded-lg bg-panel motion-reduce:animate-none" />
      ) : isMobile ? (
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <button
            type="button"
            className={cn('select-base flex min-h-11 w-full items-center justify-between gap-3 text-left focus-visible:ring-2 focus-visible:ring-neon-cyan/50', comboboxClassName)}
            disabled={isDisabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-describedby={describedBy}
            onClick={() => handleOpenChange(true)}
            onKeyDown={handleTriggerKeyDown}
          >
            <span className={cn('truncate', !displayValue && 'text-ash')}>{displayValue ?? activePlaceholder}</span>
            <ChevronsUpDownIcon aria-hidden="true" data-icon="inline-end" />
          </button>
          <SheetContent
            side="bottom"
            className="inset-0 h-dvh border-neon-cyan/15 bg-background p-0 duration-200 motion-reduce:duration-0 motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none"
            showCloseButton={false}
          >
            <SheetHeader className="border-b border-neon-cyan/10 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <SheetTitle>{label ?? 'Select option'}</SheetTitle>
                  <SheetDescription>{activePlaceholder}</SheetDescription>
                </div>
                <button type="button" className="min-h-11 rounded-lg px-3 text-sm text-neon-cyan focus-visible:ring-2 focus-visible:ring-neon-cyan/50" onClick={closeAndRestore}>
                  Cancel
                </button>
              </div>
            </SheetHeader>
            <div className="min-h-0 flex-1">{list}</div>
          </SheetContent>
        </Sheet>
      ) : (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn('select-base flex min-h-10 w-full items-center justify-between gap-3 text-left focus-visible:ring-2 focus-visible:ring-neon-cyan/50', comboboxClassName)}
              disabled={isDisabled}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              aria-describedby={describedBy}
              onKeyDown={handleTriggerKeyDown}
            >
              <span className={cn('truncate', !displayValue && 'text-ash')}>{displayValue ?? activePlaceholder}</span>
              <ChevronsUpDownIcon aria-hidden="true" data-icon="inline-end" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-(--radix-popover-trigger-width) border border-neon-cyan/15 bg-panel p-0 motion-reduce:duration-0 motion-reduce:data-closed:animate-none motion-reduce:data-open:animate-none">
            {list}
          </PopoverContent>
        </Popover>
      )}

      {allowOther && isOtherActive && (
        <input
          type="text"
          value={otherValue}
          onChange={(event) => onOtherChange?.(event.target.value)}
          placeholder={otherPlaceholder}
          className="input-base mt-2"
          disabled={isDisabled}
          aria-label={otherOptionLabel}
        />
      )}
      {cascadeLabel && <p id={statusId} className="hint-text mt-1.5" aria-live="polite">{cascadeLabel}</p>}
      {helperText && <p id={helperId} className={cn('hint-text mt-1.5', helperTextClassName)}>{helperText}</p>}
    </div>
  );
}

function TaxonomyComboboxList({
  activeDescendant,
  allowOther,
  groupByLabelMap,
  groups,
  isOtherActive,
  listId,
  onEscape,
  onSelect,
  otherOptionLabel,
  otherOptionValue,
  search,
  setOpen,
  setSearch,
  value,
  valueField,
  visibleItems,
  totalItems,
}: {
  activeDescendant?: string;
  allowOther: boolean;
  groupByLabelMap?: Record<string, string>;
  groups: [string, RankedItem[]][] | null;
  isOtherActive: boolean;
  listId: string;
  onEscape: () => void;
  onSelect: (value: string) => void;
  otherOptionLabel: string;
  otherOptionValue: string;
  search: string;
  setOpen: (open: boolean) => void;
  setSearch: (search: string) => void;
  value: string | null;
  valueField: 'code' | 'id';
  visibleItems: RankedItem[];
  totalItems: number;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (activeDescendant) {
      inputRef.current?.setAttribute('aria-activedescendant', activeDescendant);
    } else {
      inputRef.current?.removeAttribute('aria-activedescendant');
    }
  }, [activeDescendant]);

  const showLimitHint = totalItems > visibleItems.length;

  return (
    <Command shouldFilter={false} className="h-full rounded-lg bg-panel text-foreground">
      <CommandInput
        ref={inputRef}
        value={search}
        onValueChange={setSearch}
        placeholder="Type a label or code..."
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendant}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onEscape();
          }
          if (event.key === 'Tab') {
            setOpen(false);
          }
        }}
      />
      <CommandList id={listId} role="listbox" className="max-h-[min(420px,calc(100vh-14rem))] min-h-0 flex-1 scroll-py-2 p-1 sm:max-h-[360px]">
        {visibleItems.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-4 py-6 text-center text-sm text-ash">
            <span>No match for &apos;{search}&apos;</span>
          </div>
        )}
        {groups
          ? groups.map(([groupValue, groupItems]) => (
              <CommandGroup key={groupValue} role="group" aria-label={groupByLabelMap?.[groupValue] ?? groupValue} heading={groupByLabelMap?.[groupValue] ?? groupValue}>
                {groupItems.map((item) => (
                  <TaxonomyCommandItem key={item.item.id} id={`${listId}-${item.item.id}`} item={item.item} matchedAlias={item.matchedAlias} selected={optionValue(item.item, valueField) === String(value)} valueField={valueField} onSelect={onSelect} />
                ))}
              </CommandGroup>
            ))
          : visibleItems.map((item) => (
              <TaxonomyCommandItem key={item.item.id} id={`${listId}-${item.item.id}`} item={item.item} matchedAlias={item.matchedAlias} selected={optionValue(item.item, valueField) === String(value)} valueField={valueField} onSelect={onSelect} />
            ))}
        {showLimitHint && <div className="px-3 py-2 text-xs text-ash">Showing first {visibleItems.length}. Keep typing to narrow {totalItems} matches.</div>}
        {allowOther && (
          <>
            <CommandSeparator />
            <CommandItem role="option" value={otherOptionValue} aria-selected={isOtherActive} onSelect={() => onSelect(otherOptionValue)} className="min-h-11 justify-between rounded-lg px-3 py-2 text-neon-cyan data-[selected=true]:bg-neon-cyan/10 data-[selected=true]:text-neon-cyan">
              <span>{otherOptionLabel}</span>
              {isOtherActive && <CheckIcon aria-hidden="true" data-icon="inline-end" />}
            </CommandItem>
          </>
        )}
      </CommandList>
      {allowOther && visibleItems.length === 0 && (
        <div className="border-t border-border/70 p-2">
          <button
            type="button"
            className="min-h-11 w-full rounded-lg border border-neon-cyan/20 px-3 py-2 text-sm text-neon-cyan focus-visible:ring-2 focus-visible:ring-neon-cyan/50"
            onPointerDown={(event) => {
              event.preventDefault();
              onSelect(otherOptionValue);
            }}
            onClick={() => onSelect(otherOptionValue)}
          >
            Use {otherOptionLabel}
          </button>
        </div>
      )}
    </Command>
  );
}

function TaxonomyCommandItem({
  id,
  item,
  matchedAlias,
  selected,
  valueField,
  onSelect,
}: {
  id: string;
  item: TaxonomyItem;
  matchedAlias?: string;
  selected: boolean;
  valueField: 'code' | 'id';
  onSelect: (value: string) => void;
}) {
  return (
    <CommandItem
      id={id}
      role="option"
      value={`${item.label} ${item.code}`}
      aria-selected={selected}
      onSelect={() => onSelect(optionValue(item, valueField))}
      className="min-h-11 justify-between rounded-lg px-3 py-2 data-[selected=true]:bg-neon-cyan/10 data-[selected=true]:text-neon-cyan"
    >
      <span className="min-w-0 truncate">
        {item.label}
        {matchedAlias && <span className="ml-2 text-xs text-ash">matched: {matchedAlias}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-xs text-ash">
        {item.code}
        {selected && <CheckIcon aria-hidden="true" data-icon="inline-end" />}
      </span>
    </CommandItem>
  );
}

function categoryLabel(category: TaxonomyCategory) {
  return String(category)
    .replace(/([A-Z])/g, ' $1')
    .toLocaleLowerCase();
}
